/**
 * CSV Parser utility for client import
 */

export interface CSVParseResult {
  headers: string[];
  rows: Record<string, string>[];
  detectedSeparator: string;
}

export interface ColumnMapping {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  emergencyContact?: string;
  subscriptionType?: string;  // PACK
  subscriptionDate?: string;  // DATE
  paymentStatus?: string;     // INSCRIPTION (P, NP, G)
}

const KNOWN_COLUMN_MAPPINGS: Record<string, keyof ColumnMapping> = {
  // firstName mappings
  'prenom': 'firstName',
  'prénom': 'firstName',
  'firstname': 'firstName',
  'first_name': 'firstName',
  'first name': 'firstName',
  
  // lastName mappings  
  'nom': 'lastName',
  'nom de famille': 'lastName',
  'lastname': 'lastName',
  'last_name': 'lastName',
  'last name': 'lastName',
  'family name': 'lastName',
  '#name?': 'lastName', // Excel formula error - often means NOM
  
  // email mappings
  'email': 'email',
  'e-mail': 'email',
  'mail': 'email',
  'courriel': 'email',
  'adresse email': 'email',
  
  // phone mappings
  'telephone': 'phone',
  'téléphone': 'phone',
  'phone': 'phone',
  'tel': 'phone',
  'mobile': 'phone',
  'numero': 'phone',
  'numéro': 'phone',
  
  // emergency contact mappings
  'contact urgence': 'emergencyContact',
  'urgence': 'emergencyContact',
  'emergency': 'emergencyContact',
  'emergency contact': 'emergencyContact',
  'numero urgence': 'emergencyContact',
  'numéro urgence': 'emergencyContact',
  
  // subscription type (PACK)
  'pack': 'subscriptionType',
  'abonnement': 'subscriptionType',
  'formule': 'subscriptionType',
  'type abonnement': 'subscriptionType',
  'subscription': 'subscriptionType',
  'subscription type': 'subscriptionType',
  
  // subscription date
  'date': 'subscriptionDate',
  'date inscription': 'subscriptionDate',
  'date debut': 'subscriptionDate',
  'date début': 'subscriptionDate',
  'start date': 'subscriptionDate',
  
  // payment status (INSCRIPTION)
  'inscription': 'paymentStatus',
  'paiement': 'paymentStatus',
  'statut': 'paymentStatus',
  'statut paiement': 'paymentStatus',
  'payé': 'paymentStatus',
  'payment': 'paymentStatus',
  'payment status': 'paymentStatus',
};

/**
 * Detect the separator used in CSV content
 */
function detectSeparator(content: string): string {
  const firstLine = content.split('\n')[0] || '';
  const separators = [',', ';', '\t'];
  let bestSeparator = ',';
  let maxCount = 0;
  
  for (const sep of separators) {
    const count = (firstLine.match(new RegExp(sep === '\t' ? '\\\t' : sep, 'g')) || []).length;
    if (count > maxCount) {
      maxCount = count;
      bestSeparator = sep;
    }
  }
  
  return bestSeparator;
}

/**
 * Parse a CSV line handling quoted values
 */
function parseCSVLine(line: string, separator: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === separator && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
}

/**
 * Parse CSV content into structured data
 */
export function parseCSV(content: string): CSVParseResult {
  // Normalize line endings
  const normalizedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalizedContent.split('\n').filter(line => line.trim());
  
  if (lines.length === 0) {
    return { headers: [], rows: [], detectedSeparator: ',' };
  }
  
  const separator = detectSeparator(normalizedContent);
  const headers = parseCSVLine(lines[0], separator);
  
  const rows: Record<string, string>[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i], separator);
    const row: Record<string, string> = {};
    
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    
    rows.push(row);
  }
  
  return { headers, rows, detectedSeparator: separator };
}

/**
 * Auto-detect column mapping from headers using flexible partial matching
 */
export function detectColumnMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const usedHeaders = new Set<string>();
  
  // First pass: exact matching
  for (const header of headers) {
    const normalizedHeader = header.toLowerCase().trim();
    const mappedField = KNOWN_COLUMN_MAPPINGS[normalizedHeader];
    
    if (mappedField && !mapping[mappedField]) {
      mapping[mappedField] = header;
      usedHeaders.add(header);
    }
  }
  
  // Second pass: partial/flexible matching for unmapped fields
  for (const header of headers) {
    if (usedHeaders.has(header)) continue;
    
    const normalizedHeader = header.toLowerCase().trim();
    
    // lastName - look for "nom" not already mapped
    if (!mapping.lastName && (normalizedHeader === 'nom' || normalizedHeader.startsWith('#'))) {
      mapping.lastName = header;
      usedHeaders.add(header);
      continue;
    }
    
    // firstName - look for "prenom" variations
    if (!mapping.firstName && (normalizedHeader.includes('prenom') || normalizedHeader.includes('prénom'))) {
      mapping.firstName = header;
      usedHeaders.add(header);
      continue;
    }
    
    // email
    if (!mapping.email && (normalizedHeader.includes('email') || normalizedHeader.includes('mail'))) {
      mapping.email = header;
      usedHeaders.add(header);
      continue;
    }
    
    // phone - first "numero" without "urgence"
    if (!mapping.phone && 
        (normalizedHeader.includes('numero') || normalizedHeader.includes('numéro') || 
         normalizedHeader.includes('tel') || normalizedHeader.includes('phone')) &&
        !normalizedHeader.includes('urgence') && !normalizedHeader.includes('emergency')) {
      mapping.phone = header;
      usedHeaders.add(header);
      continue;
    }
    
    // emergencyContact - "numero" with "urgence"
    if (!mapping.emergencyContact && 
        (normalizedHeader.includes('urgence') || normalizedHeader.includes('emergency'))) {
      mapping.emergencyContact = header;
      usedHeaders.add(header);
      continue;
    }
    
    // subscriptionType - PACK
    if (!mapping.subscriptionType && 
        (normalizedHeader.includes('pack') || normalizedHeader.includes('abonnement') || 
         normalizedHeader.includes('formule') || normalizedHeader.includes('subscription'))) {
      mapping.subscriptionType = header;
      usedHeaders.add(header);
      continue;
    }
    
    // subscriptionDate - DATE (but not "updated_at" etc)
    if (!mapping.subscriptionDate && 
        normalizedHeader === 'date') {
      mapping.subscriptionDate = header;
      usedHeaders.add(header);
      continue;
    }
    
    // paymentStatus - INSCRIPTION
    if (!mapping.paymentStatus && 
        (normalizedHeader.includes('inscription') || normalizedHeader.includes('paiement') ||
         (normalizedHeader.includes('statut') && !normalizedHeader.includes('abonnement')))) {
      mapping.paymentStatus = header;
      usedHeaders.add(header);
      continue;
    }
  }
  
  // Special case: if we have "PRENOM" and "NOM" columns, ensure correct mapping
  const hasPrenom = headers.some(h => h.toLowerCase().includes('prenom') || h.toLowerCase().includes('prénom'));
  const hasNom = headers.some(h => h.toLowerCase() === 'nom' || h.toLowerCase().startsWith('#'));
  
  if (hasPrenom && hasNom) {
    const prenomCol = headers.find(h => h.toLowerCase().includes('prenom') || h.toLowerCase().includes('prénom'));
    const nomCol = headers.find(h => h.toLowerCase() === 'nom' || h.toLowerCase().startsWith('#'));
    if (prenomCol) mapping.firstName = prenomCol;
    if (nomCol) mapping.lastName = nomCol;
  }
  
  return mapping;
}

/**
 * Normalize phone number to standard format
 */
export function normalizePhoneNumber(phone: string): string {
  if (!phone) return '';
  
  // Notation scientifique (« 3,51915E+11 » : Excel a tronqué l'affichage).
  // parseFloat produisait un numéro d'apparence valide mais FAUX
  // (351915000000), importé sans le moindre avertissement. Les chiffres
  // perdus sont irrécupérables : on renvoie la valeur brute pour que la
  // ligne soit signalée et corrigée à la main.
  if (phone.includes('E+') || phone.includes('e+')) {
    return phone.trim();
  }
  
  // Remove all non-digit characters except + at the start
  let normalized = phone.replace(/[^\d+]/g, '');
  
  // If starts with country code, keep it
  if (normalized.startsWith('+')) {
    return normalized;
  }
  
  // If 8 digits (Tunisian local), add +216
  if (normalized.length === 8 && /^[2-9]/.test(normalized)) {
    return `+216${normalized}`;
  }
  
  // If 10+ digits and starts with country code without +
  if (normalized.length >= 10) {
    // Check for common country codes
    if (normalized.startsWith('216')) {
      return `+${normalized}`;
    }
    if (normalized.startsWith('33')) {
      return `+${normalized}`;
    }
    if (normalized.startsWith('351')) {
      return `+${normalized}`;
    }
    if (normalized.startsWith('39')) {
      return `+${normalized}`;
    }
  }
  
  return normalized;
}

/**
 * Validate email format (legacy - kept for compatibility)
 */
export function isValidEmail(email: string): boolean {
  return validateEmail(email).isValid;
}

/**
 * Detailed email validation with specific error codes
 */
export type EmailErrorCode = 
  | 'empty' 
  | 'hasSpaces' 
  | 'missingAt' 
  | 'multipleAt' 
  | 'invalidDomain' 
  | 'invalidTld' 
  | 'invalidFormat';

export function validateEmail(email: string): { isValid: boolean; errorCode?: EmailErrorCode } {
  if (!email || email.trim() === '') {
    return { isValid: false, errorCode: 'empty' };
  }
  
  const trimmed = email.trim();
  
  // Check for spaces in email
  if (trimmed.includes(' ')) {
    return { isValid: false, errorCode: 'hasSpaces' };
  }
  
  // Check for @ symbol
  if (!trimmed.includes('@')) {
    return { isValid: false, errorCode: 'missingAt' };
  }
  
  // Check for multiple @
  if ((trimmed.match(/@/g) || []).length > 1) {
    return { isValid: false, errorCode: 'multipleAt' };
  }
  
  // Check for domain
  const parts = trimmed.split('@');
  if (parts.length !== 2 || !parts[1] || !parts[1].includes('.')) {
    return { isValid: false, errorCode: 'invalidDomain' };
  }
  
  // Check for TLD
  const domainParts = parts[1].split('.');
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2) {
    return { isValid: false, errorCode: 'invalidTld' };
  }
  
  // Full regex check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    return { isValid: false, errorCode: 'invalidFormat' };
  }
  
  return { isValid: true };
}

/**
 * Clean and capitalize name
 */
export function cleanName(name: string): string {
  if (!name) return '';
  return name
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
}

/**
 * Split combined name into first and last name
 */
export function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/);
  
  if (parts.length === 1) {
    return { firstName: cleanName(parts[0]), lastName: '' };
  }
  
  if (parts.length === 2) {
    return { firstName: cleanName(parts[0]), lastName: cleanName(parts[1]) };
  }
  
  // For more than 2 parts, assume last word is last name
  const lastName = parts.pop() || '';
  const firstName = parts.join(' ');
  
  return { firstName: cleanName(firstName), lastName: cleanName(lastName) };
}

/**
 * Parse date from various formats (DD/MM/YYYY, YYYY-MM-DD, etc.)
 */
export function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  
  const trimmed = dateStr.trim();
  
  // Try DD/MM/YYYY format
  const ddmmyyyy = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (ddmmyyyy) {
    const [, day, month, year] = ddmmyyyy;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(date.getTime())) return date;
  }
  
  // Try YYYY-MM-DD format
  const yyyymmdd = trimmed.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (yyyymmdd) {
    const [, year, month, day] = yyyymmdd;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(date.getTime())) return date;
  }
  
  // Try native Date parsing as fallback
  const nativeDate = new Date(trimmed);
  if (!isNaN(nativeDate.getTime())) return nativeDate;
  
  return null;
}

/**
 * Generate CSV template content matching the expected format
 */
export function generateCSVTemplate(): string {
  const headers = ['NOM', 'PRENOM', 'NUMERO', 'EMAIL', 'NUMERO URGENCE', 'INSCRIPTION', 'PACK', 'DATE'];
  const exampleRow = ['Dupont', 'Jean', '22123456', 'jean.dupont@example.com', '98765432', 'P', 'CYGNE NOIR', '01/01/2025'];
  
  return [
    headers.join(';'),
    exampleRow.join(';'),
  ].join('\n');
}
